import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react-native'
import {
  useProfileCorankComparisonQuery,
  User,
} from '../../../../generated/graphql'
import { AuthContext } from '../../../../contexts/auth-context'
import type { CorankTrackerRunner } from '../components/corank-tracker.model'
import { corankSeriesToTimelinePoints } from '../utils/corank-series-to-timeline.util'
import Toast from 'react-native-root-toast'
import { MessageType } from '../../../../utils/message-type'
import { SocketEvents, useSocketRoom } from '../../../../socket/socket.hooks'

export type ProfileCorankTrackProfile = Pick<User, 'id' | 'username'>

/**
 * Two runners: profile user (viewed) and viewer (you).
 * `nodeId` undefined = all-public co-rank row; set to a node id for that node’s row.
 */
export function useProfileCorankTrackRunners(params: {
  profile?: ProfileCorankTrackProfile
  nodeId: string | undefined
}) {
  const { userId: myUserId, username: myUsername } = useContext(AuthContext)
  const { profile, nodeId } = params

  const shouldCompare = profile?.id !== myUserId

  const { data, error, refetch } = useProfileCorankComparisonQuery({
    variables: {
      profileUserId: profile?.id ?? '',
      viewerUserId: myUserId ?? '',
      nodeId: nodeId ?? undefined,
    },
    skip: !shouldCompare || !profile || !myUserId,
    fetchPolicy: 'cache-and-network',
  })

  const comparisonActive =
    shouldCompare && Boolean(profile?.id) && Boolean(myUserId)

  const socketRefetchCorankComparison = useCallback(() => {
    if (!comparisonActive) {
      return
    }
    setTimeout(() => {
      void refetch()
    }, 300)
  }, [comparisonActive, refetch])

  useSocketRoom(comparisonActive && profile?.id ? profile.id : undefined).event(
    SocketEvents.Refresh,
    socketRefetchCorankComparison
  )
  useSocketRoom(comparisonActive && myUserId ? myUserId : undefined).event(
    SocketEvents.Refresh,
    socketRefetchCorankComparison
  )

  useEffect(() => {
    if (!error) {
      return
    }
    Toast.show(error.message, MessageType.error)
    Sentry.captureException(error)
  }, [error])

  return useMemo(() => {
    if (!shouldCompare) {
      return []
    }

    const viewerCorank = data?.viewerCorank
    const profileCorank = data?.profileUserCorank
    if (viewerCorank === undefined || profileCorank === undefined) {
      return []
    }

    const profileTimeline = corankSeriesToTimelinePoints(
      data?.profileCorankTimeline,
      undefined,
      profileCorank
    )
    const viewerTimeline = corankSeriesToTimelinePoints(
      data?.viewerCorankTimeline,
      undefined,
      viewerCorank
    )

    const scope = nodeId ?? null
    const runners: CorankTrackerRunner[] = [
      {
        userName: profile?.username ?? '',
        userId: profile?.id,
        corankScopeNodeId: scope,
        corank: profileCorank,
        isHighlighted: false,
        ...(profileTimeline ? { timeline: profileTimeline } : {}),
      },
      {
        userName: myUsername,
        userId: myUserId,
        corankScopeNodeId: scope,
        corank: viewerCorank,
        isHighlighted: true,
        ...(viewerTimeline ? { timeline: viewerTimeline } : {}),
      },
    ]
    return runners
  }, [
    shouldCompare,
    data?.viewerCorank,
    data?.viewerCorankTimeline,
    data?.profileUserCorank,
    data?.profileCorankTimeline,
    myUserId,
    myUsername,
    profile,
  ])
}
